import { useMutation } from '@apollo/client/react';
import { DataTableAddButton } from '@optools/components/data-table/data-table-add-button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@optools/components/ui/dialog';
import { CREATE_MULTICAST_MAPPING } from '@optools/services/operator-gateway/btc/mutations';
import { SEARCH_MULTICAST_MAPPING } from '@optools/services/operator-gateway/btc/queries';
import { SearchMulticastMappingQuery } from '@optools/services/operator-gateway/types/generated/graphql';
import { useState } from 'react';
import { toast } from 'sonner';

import { MulticastMappingForm, MulticastMappingFormValues } from './multicast-mapping-form';

export function AddMulticastMapping() {
  const [isOpen, setIsOpen] = useState(false);

  const [createMulticastMapping, { loading }] = useMutation(CREATE_MULTICAST_MAPPING, {
    onCompleted: (data) => {
      if (!data?.createMulticastMapping) {
        toast.error('Create Multicast Mapping', {
          description: 'Failed to create new multicast mapping',
          duration: Infinity,
        });
        return;
      }

      toast.success('Create Multicast Mapping', {
        description: `Created new multicast mapping with id: ${data.createMulticastMapping.id}`,
      });
      setIsOpen(false);
    },
    update: (cache, { data }) => {
      if (!data?.createMulticastMapping) return;

      const multicastMappings = cache.readQuery<SearchMulticastMappingQuery>({
        query: SEARCH_MULTICAST_MAPPING,
      });

      if (!multicastMappings) return;

      const updatedMulticastMappings = [
        data.createMulticastMapping,
        ...multicastMappings.searchMulticastMappings,
      ];

      cache.writeQuery<SearchMulticastMappingQuery>({
        data: {
          searchMulticastMappings: updatedMulticastMappings,
        },
        query: SEARCH_MULTICAST_MAPPING,
      });
    },
  });

  const onSubmit = (values: MulticastMappingFormValues) =>
    createMulticastMapping({ variables: values });

  const onCancel = () => setIsOpen(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <DataTableAddButton>Add Multicast Mapping</DataTableAddButton>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Multicast Mapping</DialogTitle>
          <DialogDescription className="sr-only">Add a new multicast mapping.</DialogDescription>
        </DialogHeader>
        <MulticastMappingForm disabled={loading} onCancel={onCancel} onSubmit={onSubmit} />
      </DialogContent>
    </Dialog>
  );
}
